import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { ControlledDatepickerProps } from "../../model/types"
import type { GridCell, ViewMode } from "./types"
import {
	formatDisplayDate,
	formatDisplayMonth,
	getDaysInMonth,
	getFirstDayOfWeek,
	getMonthNames,
	getWeekdayNames,
	isDateInRange,
	isSameDay,
	isSameMonth,
} from "./utils"

export const useControlledDatepicker = ({
	value,
	onChange,
	showMonthYearPicker,
	language,
}: Pick<
	ControlledDatepickerProps,
	"value" | "onChange" | "showMonthYearPicker" | "language"
>) => {
	const [isOpen, setIsOpen] = useState(false)
	const [viewMode, setViewMode] = useState<ViewMode>(
		showMonthYearPicker ? "months" : "days",
	)
	const [isRangeMode, setIsRangeMode] = useState(() => Array.isArray(value))
	const [rangeStart, setRangeStart] = useState<Date | null>(null)
	const [hoveredDate, setHoveredDate] = useState<Date | null>(null)

	const now = new Date()
	const [currentYear, setCurrentYear] = useState(now.getFullYear())
	const [currentMonth, setCurrentMonth] = useState(now.getMonth())

	const containerRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)

	const weekdays = useMemo(() => getWeekdayNames(language), [language])
	const monthNames = useMemo(() => getMonthNames(language), [language])

	// Click outside to close
	useEffect(() => {
		if (!isOpen) return
		const handler = (e: MouseEvent) => {
			if (
				containerRef.current &&
				e.target instanceof Node &&
				!containerRef.current.contains(e.target)
			) {
				setIsOpen(false)
				setRangeStart(null)
				setHoveredDate(null)
			}
		}
		document.addEventListener("mousedown", handler)
		return () => document.removeEventListener("mousedown", handler)
	}, [isOpen])

	// Close on Escape
	useEffect(() => {
		if (!isOpen) return
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setIsOpen(false)
				setRangeStart(null)
				setHoveredDate(null)
				triggerRef.current?.focus()
			}
		}
		document.addEventListener("keydown", handler)
		return () => document.removeEventListener("keydown", handler)
	}, [isOpen])

	// Display value
	const displayValue = useMemo((): string => {
		if (!value) return ""

		const fmt =
			viewMode === "months" ? formatDisplayMonth : formatDisplayDate

		if (value instanceof Date) {
			return fmt(value, language)
		}
		if (Array.isArray(value)) {
			const [start, end] = value
			if (start && end) {
				return `${fmt(start, language)} – ${fmt(end, language)}`
			}
			if (start) {
				return fmt(start, language)
			}
		}
		return ""
	}, [value, language, viewMode])

	const toggleOpen = useCallback(() => {
		setIsOpen(prev => !prev)
	}, [])

	const handleSetViewMode = useCallback(
		(mode: ViewMode) => {
			setViewMode(mode)
			setRangeStart(null)
			setHoveredDate(null)
			onChange(null)
		},
		[onChange],
	)

	// Day selection
	const handleDayClick = useCallback(
		(day: number) => {
			const clicked = new Date(currentYear, currentMonth, day)
			if (isRangeMode) {
				if (rangeStart) {
					const start = new Date(
						Math.min(rangeStart.getTime(), clicked.getTime()),
					)
					const end = new Date(
						Math.max(rangeStart.getTime(), clicked.getTime()),
					)
					onChange([start, end])
					setRangeStart(null)
					setHoveredDate(null)
					setIsOpen(false)
				} else {
					setRangeStart(clicked)
					onChange(null)
				}
			} else {
				onChange(clicked)
				setIsOpen(false)
			}
		},
		[currentYear, currentMonth, isRangeMode, rangeStart, onChange],
	)

	// Month selection
	const handleMonthClick = useCallback(
		(monthIndex: number) => {
			const clicked = new Date(currentYear, monthIndex, 1)
			if (isRangeMode) {
				if (rangeStart) {
					const start = new Date(
						Math.min(rangeStart.getTime(), clicked.getTime()),
					)
					const endMonth = new Date(
						Math.max(rangeStart.getTime(), clicked.getTime()),
					)
					const end = new Date(
						endMonth.getFullYear(),
						endMonth.getMonth() + 1,
						0,
					)
					onChange([start, end])
					setRangeStart(null)
					setHoveredDate(null)
					setIsOpen(false)
				} else {
					setRangeStart(clicked)
					onChange(null)
				}
			} else {
				onChange(clicked)
				setIsOpen(false)
			}
		},
		[currentYear, isRangeMode, rangeStart, onChange],
	)

	// Toggle range mode
	const handleRangeToggle = useCallback(() => {
		setRangeStart(null)
		setHoveredDate(null)
		setIsRangeMode(prev => !prev)
		onChange(null)
	}, [onChange])

	// Navigation
	const handlePrev = useCallback(() => {
		if (viewMode === "months") {
			setCurrentYear(y => y - 1)
		} else {
			setCurrentMonth(m => {
				if (m === 0) {
					setCurrentYear(y => y - 1)
					return 11
				}
				return m - 1
			})
		}
	}, [viewMode])

	const handleNext = useCallback(() => {
		if (viewMode === "months") {
			setCurrentYear(y => y + 1)
		} else {
			setCurrentMonth(m => {
				if (m === 11) {
					setCurrentYear(y => y + 1)
					return 0
				}
				return m + 1
			})
		}
	}, [viewMode])

	// Days grid
	const daysGrid = useMemo((): GridCell[] => {
		const daysInMonth = getDaysInMonth(currentYear, currentMonth)
		const firstDay = getFirstDayOfWeek(currentYear, currentMonth)
		const prevMonthDays = getDaysInMonth(
			currentMonth === 0 ? currentYear - 1 : currentYear,
			currentMonth === 0 ? 11 : currentMonth - 1,
		)
		const cells: GridCell[] = []
		for (let i = firstDay - 1; i >= 0; i--) {
			cells.push({ day: prevMonthDays - i, isCurrentMonth: false })
		}
		for (let d = 1; d <= daysInMonth; d++) {
			cells.push({ day: d, isCurrentMonth: true })
		}
		const remaining = 42 - cells.length
		for (let d = 1; d <= remaining; d++) {
			cells.push({ day: d, isCurrentMonth: false })
		}
		return cells
	}, [currentYear, currentMonth])

	// Selection helpers
	const isDaySelected = useCallback(
		(day: number): boolean => {
			const date = new Date(currentYear, currentMonth, day)
			if (rangeStart && isSameDay(date, rangeStart)) return true
			if (rangeStart && hoveredDate && isSameDay(date, hoveredDate))
				return true
			if (isRangeMode && Array.isArray(value)) {
				const [s, e] = value
				return (
					(s != null && isSameDay(date, s)) ||
					(e != null && isSameDay(date, e))
				)
			}
			return value instanceof Date && isSameDay(date, value)
		},
		[
			currentYear,
			currentMonth,
			isRangeMode,
			value,
			rangeStart,
			hoveredDate,
		],
	)

	const isDayInRange = useCallback(
		(day: number): boolean => {
			if (!isRangeMode) return false
			const date = new Date(currentYear, currentMonth, day)
			if (Array.isArray(value) && value[0] && value[1]) {
				return isDateInRange(date, value[0], value[1])
			}
			if (rangeStart && hoveredDate) {
				const start = new Date(
					Math.min(rangeStart.getTime(), hoveredDate.getTime()),
				)
				const end = new Date(
					Math.max(rangeStart.getTime(), hoveredDate.getTime()),
				)
				return isDateInRange(date, start, end)
			}
			return false
		},
		[
			currentYear,
			currentMonth,
			isRangeMode,
			value,
			rangeStart,
			hoveredDate,
		],
	)

	const isMonthSelected = useCallback(
		(monthIndex: number): boolean => {
			const date = new Date(currentYear, monthIndex, 1)
			if (rangeStart && isSameMonth(date, rangeStart)) return true
			if (rangeStart && hoveredDate && isSameMonth(date, hoveredDate))
				return true
			if (isRangeMode && Array.isArray(value)) {
				const [s, e] = value
				return (
					(s != null && isSameMonth(date, s)) ||
					(e != null && isSameMonth(date, e))
				)
			}
			return value instanceof Date && isSameMonth(date, value)
		},
		[currentYear, isRangeMode, value, rangeStart, hoveredDate],
	)

	const isMonthInRange = useCallback(
		(monthIndex: number): boolean => {
			if (!isRangeMode) return false
			const date = new Date(currentYear, monthIndex, 15)
			if (Array.isArray(value) && value[0] && value[1]) {
				return isDateInRange(date, value[0], value[1])
			}
			if (rangeStart && hoveredDate) {
				const start = new Date(
					Math.min(rangeStart.getTime(), hoveredDate.getTime()),
				)
				const end = new Date(
					Math.max(rangeStart.getTime(), hoveredDate.getTime()),
				)
				return isDateInRange(date, start, end)
			}
			return false
		},
		[currentYear, isRangeMode, value, rangeStart, hoveredDate],
	)

	const clearHoveredDate = useCallback(() => {
		setHoveredDate(null)
	}, [])

	const handleDayHover = useCallback(
		(day: number) => {
			if (rangeStart) {
				setHoveredDate(new Date(currentYear, currentMonth, day))
			}
		},
		[rangeStart, currentYear, currentMonth],
	)

	const handleMonthHover = useCallback(
		(monthIndex: number) => {
			if (rangeStart) {
				setHoveredDate(new Date(currentYear, monthIndex, 1))
			}
		},
		[rangeStart, currentYear],
	)

	return {
		isOpen,
		viewMode,
		isRangeMode,
		currentYear,
		currentMonth,
		displayValue,
		weekdays,
		monthNames,
		daysGrid,
		containerRef,
		triggerRef,
		toggleOpen,
		handleSetViewMode,
		handlePrev,
		handleNext,
		handleDayClick,
		handleMonthClick,
		handleRangeToggle,
		clearHoveredDate,
		handleDayHover,
		handleMonthHover,
		isDaySelected,
		isDayInRange,
		isMonthSelected,
		isMonthInRange,
	}
}
